<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import GenericModal from "@/Components/Generics/GenericModal.vue";
import MainPage from "@/Components/MainPage.vue";
import PaginationNew from "@/Components/PaginationNew.vue";
import Authenticated from "@/Layouts/Authenticated.vue";
import { CompanyId, CompanySearchResultsWithoutData, Policies } from "@/Types";
import { Link } from "@inertiajs/inertia-vue3";
import axios from "axios";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    name: "CompaniesVue",
    components: {
        MainPage,
        PaginationNew,
        GenericButton,
        GenericModal,
        LinkVue: Link,
    },
    layout: Authenticated,
    props: {
        companies: {
            type: Object as PropType<CompanySearchResultsWithoutData>,
            required: true,
        },
        policies: {
            type: Object as PropType<Policies>,
            required: true,
        },
    },
    data() {
        return {
            page: 1,

            loading: false,
            deleteCompanyModalOpen: false,
            deleteCompanyId: null as number,
            deleteAccountModalOpen: false,
        };
    },
    methods: {
        clickRoute(hrefRoute: string): void {
            this.$inertia.visit(route(hrefRoute));
        },

        openDeleteCompanyModal(companyId: CompanyId): void {
            this.deleteCompanyModalOpen = true;
            this.deleteCompanyId = companyId;
        },
        closeDeleteCompanyModal(): void {
            this.deleteCompanyModalOpen = false;
            this.deleteCompanyId = null;
        },

        openDeleteAccountModal(): void {
            this.deleteAccountModalOpen = true;
        },
        closeDeleteAccountModal(): void {
            this.deleteAccountModalOpen = false;
        },

        loadPage(page = false) {
            this.$inertia.reload({
                data: { page: page === false ? this.companies.current_page : page },
                only: ["companies"],
            });
        },

        deleteAction(): void {
            axios
                .delete(route("companies.destroy", { company: this.deleteCompanyId }))
                .then(() => {
                    this.$inertia.reload({
                        data: { page: this.companies.current_page },
                        only: ["companies"],
                    });
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.closeDeleteCompanyModal();
                });
        },
        deleteAccountAction(): void {
            axios
                .post(route("user.delete"))
                .then(() => {
                    this.$inertia.visit("/");
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.closeDeleteAccountModal();
                });
        },
    },
});
</script>

<template>
    <MainPage :title="$trans('navigation.nav_acp_companies')">
        <div class="flex flex--width flex--v flex--valign-c">
            <table class="table table-dark table-striped">
                <thead>
                    <tr>
                        <th scope="col">{{ $trans("company.id") }}</th>
                        <th scope="col">{{ $trans("company.name") }}</th>
                        <th scope="col" class="text-center">{{ $trans("company.active") }}</th>
                        <th scope="col" class="text-center"></th>
                        <th scope="col" class="text-center">{{ $trans("acp.options") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="company in companies.data" :key="company.id">
                        <th scope="row">{{ company.id }}</th>
                        <td>{{ company.name }}</td>
                        <td class="text-center align-middle">
                            <i v-if="company.active" class="fas fa-check"></i>
                            <i v-else class="fas fa-times"></i>
                        </td>
                        <td class="text-end align-middle">
                            <LinkVue v-if="policies.COMPANY_CREATE" :href="route('companies.create', { copy: company.id })" type="button" class="btn btn-info" :preserve-state="false">{{ $trans("acp.company_create_second") }}</LinkVue>
                        </td>
                        <td class="text-center align-middle">
                            <inertia-link v-if="policies.COMPANY_CREATE" :href="route('companies.edit', { company: company.id })" class="text-light cursor--pointer"><i class="far fa-edit fa-lg"></i></inertia-link>
                            <inertia-link v-if="policies.COMPANY_CREATE" :href="route('companies.create', { copy: company.id })" class="text-light cursor--pointer"><i class="far fa-copy fa-lg"></i></inertia-link>
                            <a v-if="policies.COMPANY_DELETE" class="text-light cursor--pointer" @click="openDeleteCompanyModal(company.id)"><i class="far fa-trash-alt fa-lg"></i></a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <PaginationNew v-if="companies.data.length > 0" :pagination-data="companies" class="pagination-new" @paginate="loadPage" />
        </div>

        <div class="buttons-flex-list flex flex--width flex--h flex--halign-c flex--valign-c flex--gap-10">
            <GenericButton :title="$trans('acp.company_create')" @click="clickRoute('companies.create')" />
            <GenericButton :title="$trans('auth.account_delete')" :danger="true" @click="openDeleteAccountModal()" />
        </div>

        <GenericModal v-if="deleteCompanyModalOpen" @closed="closeDeleteCompanyModal()">
            <template #title>{{ $trans("acp.title_modal_delete_company") }}</template>
            <template #content>
                <div class="flex flex--v flex--valign-c text text--size-18 text--height-24 text--primary-b text--medium text--center">{{ $trans("acp.body_modal_delete_company") }}</div>
            </template>
            <template #buttons>
                <GenericButton :title="$trans('acp.close')" :disabled="loading" :circular="true" :click-stop="false" />
                <GenericButton :title="$trans('acp.delete')" :disabled="loading" :circular="true" :danger="true" @click.stop="deleteAction()" />
            </template>
        </GenericModal>

        <GenericModal v-if="deleteAccountModalOpen" @closed="closeDeleteAccountModal()">
            <template #title>{{ $trans("auth.title_modal_delete_account") }}</template>
            <template #content>
                <div class="flex flex--v flex--valign-c text text--size-18 text--height-24 text--primary-b text--medium text--center">{{ $trans("auth.body_modal_delete_account") }}</div>
            </template>
            <template #buttons>
                <GenericButton :title="$trans('acp.close')" :disabled="loading" :circular="true" :click-stop="false" />
                <GenericButton :title="$trans('auth.account_delete')" :disabled="loading" :circular="true" :danger="true" @click.stop="deleteAccountAction()" />
            </template>
        </GenericModal>
    </MainPage>
</template>

<style lang="scss" scoped>
tbody i {
    margin-left: 10px;
}
@include media-breakpoint-down(md) {
    .buttons-flex-list {
        flex-direction: column;
    }
}
</style>
